import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LogOut, Users, Info, Calendar, BarChart3, UserPlus } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { emitChatRead } from '../lib/unreadBus';
import ChatUI from '../components/ChatUI';
import GroupInfoSheet from '../components/GroupInfoSheet';
import SharePlanPicker from '../components/SharePlanPicker';
import CreatePollModal from '../components/CreatePollModal';
import PlanChatRsvpBar from '../components/PlanChatRsvpBar';

const FAST_POLL_MS = 1500;
const SLOW_POLL_MS = 6000;

export default function GroupChat() {
  const { id: groupId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showSharePlan, setShowSharePlan] = useState(false);
  const [showCreatePoll, setShowCreatePoll] = useState(false);
  const isHidden = useRef(false);

  const fetchGroup = useCallback(async () => {
    try {
      const { data } = await api.get(`/groups/${groupId}`);
      setGroup(data);
    } catch { navigate(-1); }
  }, [groupId, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/groups/${groupId}/messages`);
      setMessages(data || []);
      setLoading(false);
    } catch { setLoading(false); }
  }, [groupId]);

  const fetchPinned = useCallback(async () => {
    try {
      const { data } = await api.get(`/groups/${groupId}/pinned`);
      setPinnedMessages(data || []);
    } catch {/* ignore */}
  }, [groupId]);

  const fetchTyping = useCallback(async () => {
    try {
      const { data } = await api.get(`/groups/${groupId}/typing`);
      setTypingUsers(data || []);
    } catch {/* ignore */}
  }, [groupId]);

  useEffect(() => { fetchGroup(); fetchMessages(); fetchPinned(); }, [fetchGroup, fetchMessages, fetchPinned]);

  useEffect(() => {
    let interval = null;
    const tick = () => { if (!isHidden.current) { fetchMessages(); fetchTyping(); } };
    const setUp = () => {
      if (interval) clearInterval(interval);
      const ms = isHidden.current ? SLOW_POLL_MS : FAST_POLL_MS;
      interval = setInterval(tick, ms);
    };
    const onVis = () => {
      isHidden.current = document.hidden;
      if (!document.hidden) {
        fetchMessages();
        fetchGroup();
        fetchPinned();
      }
      setUp();
    };
    document.addEventListener('visibilitychange', onVis);
    setUp();
    return () => { if (interval) clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchMessages, fetchTyping, fetchGroup, fetchPinned]);

  const onMarkRead = useCallback(async () => {
    try {
      const { data } = await api.post(`/groups/${groupId}/read`);
      emitChatRead({ type: 'group', id: groupId });
      // Plan-linked groups also need to clear the Activity → Messages plan
      // row (it's keyed by plan_id, not group_id). Backend already mirrors
      // last_read into `chat_reads`; this emit makes the frontend match
      // optimistically.
      if (data?.plan_id) {
        emitChatRead({ type: 'plan', id: data.plan_id });
      }
    } catch { /* swallow — badge will refresh on next poll */ }
  }, [groupId]);

  const onSend = async (text, replyTo, extras) => {
    const body = { text };
    if (replyTo) body.reply_to = replyTo;
    if (extras?.media_url) { body.media_url = extras.media_url; body.media_type = extras.media_type || 'image'; }
    if (extras?.location) body.location = extras.location;
    if (extras?.audio_url) { body.audio_url = extras.audio_url; body.audio_duration = extras.audio_duration || 0; }
    if (extras?.plan_share) body.plan_share = extras.plan_share;
    if (extras?.poll) body.poll = extras.poll;
    const { data } = await api.post(`/groups/${groupId}/messages`, body);
    setMessages(prev => [...prev, data]);
  };

  const onTyping = useCallback(() => {
    api.post(`/groups/${groupId}/typing`).catch(() => {});
  }, [groupId]);

  const onReact = useCallback(async (messageId, emoji) => {
    try {
      const { data } = await api.post(`/groups/${groupId}/messages/${messageId}/react`, { emoji });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: data.reactions } : m));
    } catch {/* ignore */}
  }, [groupId]);

  const onVote = useCallback(async (messageId, optionId) => {
    try {
      const { data } = await api.post(`/groups/${groupId}/messages/${messageId}/vote`, { option_id: optionId });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, poll: { ...m.poll, votes: data.votes } } : m));
    } catch {/* ignore */}
  }, [groupId]);

  const onPin = useCallback(async (messageId) => {
    try {
      await api.post(`/groups/${groupId}/messages/${messageId}/pin`);
      fetchPinned();
    } catch {/* ignore */}
  }, [groupId, fetchPinned]);

  const onDeleteMessage = useCallback(async (messageId) => {
    try {
      await api.delete(`/groups/${groupId}/messages/${messageId}`);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, deleted: true, text: '', media_url: '', audio_url: '', location: null, plan_share: null, poll: null } : m));
      fetchPinned();
    } catch {/* ignore */}
  }, [groupId, fetchPinned]);

  const handleLeave = async () => {
    if (!currentUser?.id) return;
    try { await api.delete(`/groups/${groupId}/members/${currentUser.id}`); } catch {/* ignore */}
    navigate('/activity', { replace: true });
  };

  const handleSharePlan = async (plan) => {
    setShowSharePlan(false);
    await onSend('', null, { plan_share: { plan_id: plan.id } });
  };

  const handleCreatePoll = async (poll) => {
    setShowCreatePoll(false);
    await onSend('', null, { poll });
  };

  if (loading || !group) {
    return (
      <div className="fixed inset-0 bg-[#FAFAF5] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#9B9EA3] border-t-[#111] rounded-full animate-spin" />
      </div>
    );
  }

  const memberLabel = `${group.member_count} member${group.member_count === 1 ? '' : 's'}`;
  const isPlanChat = group.kind === 'plan';
  const isCreator = group.creator_id === currentUser?.id;

  const menuItems = [
    { key: 'info', label: 'Group info', icon: Info, onClick: () => setShowInfo(true) },
    { key: 'members', label: 'View members', icon: Users, onClick: () => setShowInfo(true) },
    ...(isPlanChat ? [{ key: 'invite', label: 'Invite friends', icon: UserPlus, onClick: () => setShowInfo(true) }] : []),
    { key: 'leave', label: 'Leave group', icon: LogOut, destructive: true, onClick: () => setShowLeaveConfirm(true) },
  ];

  const composerExtras = [
    { key: 'plan', label: 'Share a plan', icon: Calendar, desc: 'Drop a plan card with Join button', onClick: () => setShowSharePlan(true) },
    { key: 'poll', label: 'Create poll', icon: BarChart3, desc: 'Decide where, when or what together', onClick: () => setShowCreatePoll(true) },
  ];

  return (
    <>
      <ChatUI
        title={group.name}
        subtitle={memberLabel}
        avatar={group.photo}
        onBack={() => navigate(-1)}
        headerOnClick={() => setShowInfo(true)}
        messages={messages}
        currentUserId={currentUser?.id}
        isDM={false}
        onSend={onSend}
        onMarkRead={onMarkRead}
        menuItems={menuItems}
        onTyping={onTyping}
        typingUsers={typingUsers}
        onReact={onReact}
        onDeleteMessage={onDeleteMessage}
        onVote={onVote}
        onPin={onPin}
        pinnedMessages={pinnedMessages}
        isCreator={isCreator}
        composerExtras={composerExtras}
        headerPrefix={isPlanChat && group.plan_id ? <PlanChatRsvpBar planId={group.plan_id} isHost={isCreator} /> : null}
      />

      {showInfo && (
        <GroupInfoSheet
          group={group}
          currentUserId={currentUser?.id}
          onClose={() => setShowInfo(false)}
          onUpdated={(g) => setGroup(g)}
        />
      )}

      {showSharePlan && (
        <SharePlanPicker
          onClose={() => setShowSharePlan(false)}
          onPick={handleSharePlan}
        />
      )}

      {showCreatePoll && (
        <CreatePollModal
          onClose={() => setShowCreatePoll(false)}
          onCreate={handleCreatePoll}
        />
      )}

      {showLeaveConfirm && (
        <div className="fixed inset-0 z-[10080] flex items-center justify-center px-6 bg-black/50 backdrop-blur-[2px] animate-fade-in" onClick={() => setShowLeaveConfirm(false)}>
          <div className="w-full max-w-[340px] bg-white rounded-3xl shadow-2xl p-6 animate-scale-in" onClick={e => e.stopPropagation()} data-testid="group-leave-confirm">
            <h3 className="text-base font-bold text-[#111111] mb-1">Leave group?</h3>
            <p className="text-sm text-[#6B6E73] mb-5">You won't see new messages from "{group.name}".</p>
            <div className="flex flex-col gap-2">
              <button onClick={handleLeave} className="w-full bg-[#FF3B30] text-white font-bold rounded-full text-sm active:scale-[0.98]" style={{ minHeight: 52 }} data-testid="group-leave-confirm-btn">Leave group</button>
              <button onClick={() => setShowLeaveConfirm(false)} className="w-full text-[#111111] font-semibold rounded-full text-sm" style={{ minHeight: 48 }}>Stay</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
